import {
  PositionError,
  type DevicePosition,
  type LocationPermission,
  type OsmRequestInit,
  type OsmResponse,
  type PhotoMetadata,
  type Platform,
} from './types';

const EMPTY_METADATA: PhotoMetadata = { position: null, takenAt: null };

function validCoordinate(lat: unknown, lng: unknown): { lat: number; lng: number } | null {
  if (typeof lat !== 'number' || typeof lng !== 'number') return null;
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  if (Math.abs(lat) > 90 || Math.abs(lng) > 180) return null;
  // Cameras without a fix often write 0/0.
  if (lat === 0 && lng === 0) return null;
  return { lat, lng };
}

/** EXIF position and capture time. exifr is loaded on demand to keep the main bundle small. */
export async function readExifMetadata(file: Blob): Promise<PhotoMetadata> {
  try {
    const exifr = await import('exifr');
    const data = (await exifr.parse(file, {
      gps: true,
      pick: ['DateTimeOriginal', 'CreateDate', 'latitude', 'longitude'],
    })) as {
      latitude?: number;
      longitude?: number;
      DateTimeOriginal?: Date;
      CreateDate?: Date;
    } | null;
    if (!data) return EMPTY_METADATA;
    const taken = data.DateTimeOriginal ?? data.CreateDate ?? null;
    const takenAt =
      taken instanceof Date && !Number.isNaN(taken.getTime()) && taken.getTime() <= Date.now()
        ? taken
        : null;
    return { position: validCoordinate(data.latitude, data.longitude), takenAt };
  } catch {
    return EMPTY_METADATA;
  }
}

export const webPlatform: Platform = {
  kind: 'web',

  getCurrentPosition({ timeoutMs = 10_000, maximumAgeMs = 60_000 } = {}): Promise<DevicePosition> {
    return new Promise((resolve, reject) => {
      if (typeof navigator === 'undefined' || !navigator.geolocation) {
        reject(new PositionError('unsupported'));
        return;
      }
      navigator.geolocation.getCurrentPosition(
        (pos) =>
          resolve({
            lat: pos.coords.latitude,
            lng: pos.coords.longitude,
            accuracyM: Number.isFinite(pos.coords.accuracy)
              ? Math.round(pos.coords.accuracy)
              : null,
            timestamp: pos.timestamp,
          }),
        (error) =>
          reject(
            new PositionError(
              error.code === error.PERMISSION_DENIED
                ? 'denied'
                : error.code === error.TIMEOUT
                  ? 'timeout'
                  : 'unavailable',
              error.message,
            ),
          ),
        { enableHighAccuracy: true, timeout: timeoutMs, maximumAge: maximumAgeMs },
      );
    });
  },

  async locationPermission(): Promise<LocationPermission> {
    try {
      if (!navigator.permissions?.query) return 'unknown';
      const status = await navigator.permissions.query({ name: 'geolocation' });
      return status.state;
    } catch {
      return 'unknown';
    }
  },

  readPhotoMetadata: readExifMetadata,

  // Browsers use <input type="file">; the capture form renders it.
  pickPhotos: async () => null,

  isOnline: () => (typeof navigator === 'undefined' ? true : navigator.onLine),

  onConnectivityChange(listener) {
    const on = () => listener(true);
    const off = () => listener(false);
    window.addEventListener('online', on);
    window.addEventListener('offline', off);
    return () => {
      window.removeEventListener('online', on);
      window.removeEventListener('offline', off);
    };
  },

  onResume(listener) {
    const handler = () => {
      if (document.visibilityState === 'visible') listener();
    };
    document.addEventListener('visibilitychange', handler);
    return () => document.removeEventListener('visibilitychange', handler);
  },

  async osmFetch(url: string, init: OsmRequestInit = {}): Promise<OsmResponse> {
    const res = await fetch(url, {
      method: init.method ?? 'GET',
      body: init.body,
      headers: init.headers,
      signal: init.signal,
      // No cookies or credentials ever go to OpenStreetMap.
      credentials: 'omit',
      referrerPolicy: 'strict-origin-when-cross-origin',
    });
    return { status: res.status, json: () => res.json() as Promise<unknown> };
  },

  scanBarcodeNative: async () => 'unsupported',

  createBarcodeDetector: () => import('./barcode').then((m) => m.createWebBarcodeDetector()),
};
